import {
  clampCropPosition,
  getContainScale,
  getCropAreaFromViewport,
  getMaxZoomForImage,
  IMAGE_CROP_MIN_ZOOM,
  loadImage,
  type CropAreaPixels,
  type CropViewportState,
} from "./cropImageFile";
import {
  getLinkLayoutCropEntry,
  isFlatPersistedImageCrop,
  isKeyedLinkImageCrop,
  type CropViewportSize,
  type LinkCropLayout,
} from "./cropStatePersist";
import { resolveCropViewportStateForEdit } from "./resolveCropViewportStateForEdit";

export function oppositeLinkLayout(layout: LinkCropLayout): LinkCropLayout {
  return layout === "grid" ? "line" : "grid";
}

/**
 * Grow `source` to `targetAspect`, anchored at the source top-left.
 * Always fully contains `source`. Extra area is added only to the right and/or
 * bottom (never left/top). The result may extend outside the original image.
 * @deprecated Prefer shrinkCropAreaToAspectCovering for layout switches.
 */
export function expandCropAreaToAspectContaining(
  source: CropAreaPixels,
  targetAspect: number
): CropAreaPixels {
  if (!(source.width > 0 && source.height > 0 && targetAspect > 0)) {
    return source;
  }

  const srcAspect = source.width / source.height;
  let newW: number;
  let newH: number;

  if (srcAspect < targetAspect) {
    // Need wider — keep full height; extend right only
    newH = source.height;
    newW = source.height * targetAspect;
  } else if (srcAspect > targetAspect) {
    // Need taller — keep full width; extend bottom only
    newW = source.width;
    newH = source.width / targetAspect;
  } else {
    newW = source.width;
    newH = source.height;
  }

  return {
    x: source.x,
    y: source.y,
    width: newW,
    height: newH,
  };
}

/**
 * Shrink `source` to `targetAspect` with a cover fit (no letterbox).
 * Clips only the overflowing axis, splitting the excess equally on both edges
 * (left/right or top/bottom). Result always stays inside `source`.
 */
export function shrinkCropAreaToAspectCovering(
  source: CropAreaPixels,
  targetAspect: number
): CropAreaPixels {
  if (!(source.width > 0 && source.height > 0 && targetAspect > 0)) {
    return source;
  }

  const srcAspect = source.width / source.height;

  if (Math.abs(srcAspect - targetAspect) < 1e-9) {
    return {
      x: source.x,
      y: source.y,
      width: source.width,
      height: source.height,
    };
  }

  if (srcAspect > targetAspect) {
    // Wider than target — clip left and right equally
    const newH = source.height;
    const newW = source.height * targetAspect;
    return {
      x: source.x + (source.width - newW) / 2,
      y: source.y,
      width: newW,
      height: newH,
    };
  }

  // Taller than target — clip top and bottom equally
  const newW = source.width;
  const newH = source.width / targetAspect;
  return {
    x: source.x,
    y: source.y + (source.height - newH) / 2,
    width: newW,
    height: newH,
  };
}

/**
 * Layout-switch frame: prefer pulling extra pixels from the original so the
 * previous visible content is preserved, then only cover-clip if the bitmap
 * cannot contain that larger target-aspect window.
 *
 * - Grid → square: grow top/bottom into the original (keeps full grid width)
 * - Square → grid: grow left/right into the original (keeps full square height)
 * Expansion is split equally on both edges; no letterbox.
 */
export function fitContentToAspectUsingOriginal(
  content: CropAreaPixels,
  targetAspect: number,
  imageWidth: number,
  imageHeight: number
): CropAreaPixels {
  if (
    !(
      content.width > 0 &&
      content.height > 0 &&
      targetAspect > 0 &&
      imageWidth > 0 &&
      imageHeight > 0
    )
  ) {
    return content;
  }

  const srcAspect = content.width / content.height;
  let needW: number;
  let needH: number;

  if (Math.abs(srcAspect - targetAspect) < 1e-9) {
    needW = content.width;
    needH = content.height;
  } else if (srcAspect > targetAspect) {
    // Wider than target — grow vertically into the original
    needW = content.width;
    needH = content.width / targetAspect;
  } else {
    // Taller than target — grow horizontally into the original
    needH = content.height;
    needW = content.height * targetAspect;
  }

  const centerX = content.x + content.width / 2;
  const centerY = content.y + content.height / 2;

  // Fully contain prior content by taking extra from the original when it fits.
  if (needW <= imageWidth + 1e-6 && needH <= imageHeight + 1e-6) {
    const maxX = imageWidth - needW;
    const maxY = imageHeight - needH;
    // Ranges that keep `content` inside the new frame
    const minXForContent = content.x + content.width - needW;
    const maxXForContent = content.x;
    const minYForContent = content.y + content.height - needH;
    const maxYForContent = content.y;

    let x = centerX - needW / 2;
    let y = centerY - needH / 2;
    x = Math.min(Math.max(x, 0), maxX);
    y = Math.min(Math.max(y, 0), maxY);
    x = Math.min(Math.max(x, minXForContent), maxXForContent);
    y = Math.min(Math.max(y, minYForContent), maxYForContent);
    x = Math.min(Math.max(x, 0), maxX);
    y = Math.min(Math.max(y, 0), maxY);

    return { x, y, width: needW, height: needH };
  }

  // Original cannot contain the full prior crop at target aspect — use the
  // largest in-image target-aspect window centered on the prior content
  // (pulls max extra from original; equal clip of whatever still overflows).
  let width = imageWidth;
  let height = width / targetAspect;
  if (height > imageHeight) {
    height = imageHeight;
    width = height * targetAspect;
  }

  return clampCoverFrameInImage(
    {
      x: centerX - width / 2,
      y: centerY - height / 2,
      width,
      height,
    },
    imageWidth,
    imageHeight
  );
}

/**
 * Fit a top-left-anchored target-aspect frame into the image for cropState.
 * Prefers keeping the frame's top-left on `source` (no left/top padding).
 * If the frame is larger than the image, use the largest in-image target-aspect
 * rect anchored at the source top-left (clamped into bounds).
 * @deprecated Prefer clampCoverFrameInImage for layout switches.
 */
export function fitContainingFrameInImage(
  frame: CropAreaPixels,
  source: CropAreaPixels,
  imageWidth: number,
  imageHeight: number
): CropAreaPixels {
  if (frame.width <= imageWidth + 1e-6 && frame.height <= imageHeight + 1e-6) {
    const maxX = Math.max(0, imageWidth - frame.width);
    const maxY = Math.max(0, imageHeight - frame.height);
    // Keep top-left on the previous crop when possible; only shift if needed
    // to stay inside the bitmap (still never invent left/top margin vs source).
    const x = Math.min(Math.max(source.x, 0), maxX);
    const y = Math.min(Math.max(source.y, 0), maxY);
    return { x, y, width: frame.width, height: frame.height };
  }

  // Frame does not fit — largest target-aspect window, top-left near source.
  const targetAspect = frame.width / frame.height;
  let newW = imageWidth;
  let newH = newW / targetAspect;
  if (newH > imageHeight) {
    newH = imageHeight;
    newW = newH * targetAspect;
  }
  const x = Math.min(Math.max(source.x, 0), Math.max(0, imageWidth - newW));
  const y = Math.min(Math.max(source.y, 0), Math.max(0, imageHeight - newH));
  return { x, y, width: newW, height: newH };
}

/**
 * Keep a cover frame inside the bitmap. Prefers the frame's current center so
 * equal edge clipping from shrinkCropAreaToAspectCovering is preserved when a
 * nudge is needed.
 */
export function clampCoverFrameInImage(
  frame: CropAreaPixels,
  imageWidth: number,
  imageHeight: number
): CropAreaPixels {
  if (!(frame.width > 0 && frame.height > 0)) {
    return frame;
  }

  const targetAspect = frame.width / frame.height;
  let width = Math.min(frame.width, imageWidth);
  let height = width / targetAspect;
  if (height > imageHeight) {
    height = imageHeight;
    width = height * targetAspect;
  }

  const centerX = frame.x + frame.width / 2;
  const centerY = frame.y + frame.height / 2;
  const maxX = Math.max(0, imageWidth - width);
  const maxY = Math.max(0, imageHeight - height);
  const x = Math.min(Math.max(centerX - width / 2, 0), maxX);
  const y = Math.min(Math.max(centerY - height / 2, 0), maxY);

  return { x, y, width, height };
}

/** Natural-image crop rect implied by viewport zoom/pan. */
export function getNaturalCropAreaFromViewportState(
  cropState: CropViewportState,
  naturalWidth: number,
  naturalHeight: number,
  viewport: CropViewportSize
): CropAreaPixels {
  const containScale = getContainScale(
    naturalWidth,
    naturalHeight,
    viewport.width,
    viewport.height
  );
  const scale = containScale * cropState.zoom;
  return getCropAreaFromViewport(
    cropState.position,
    scale,
    viewport.width,
    viewport.height
  );
}

/**
 * Build zoom/pan for a natural-image window — inverse of ImageCropperModal
 * (containScale * zoom + pan). Position is centered when the image is contained.
 */
export function getViewportStateFromNaturalCropArea(
  area: CropAreaPixels,
  naturalWidth: number,
  naturalHeight: number,
  viewport: CropViewportSize
): CropViewportState {
  const containScale = getContainScale(
    naturalWidth,
    naturalHeight,
    viewport.width,
    viewport.height
  );
  if (!(containScale > 0 && area.width > 0 && area.height > 0)) {
    return { zoom: IMAGE_CROP_MIN_ZOOM, position: { x: 0, y: 0 } };
  }

  const maxZoom = getMaxZoomForImage(
    naturalWidth,
    naturalHeight,
    viewport.width,
    viewport.height
  );
  let zoom = viewport.width / area.width / containScale;
  zoom = Math.max(IMAGE_CROP_MIN_ZOOM, Math.min(maxZoom, zoom));
  const effectiveScale = containScale * zoom;

  const position = clampCropPosition(
    -area.x * effectiveScale,
    -area.y * effectiveScale,
    effectiveScale,
    naturalWidth,
    naturalHeight,
    viewport.width,
    viewport.height,
    false
  );

  return { zoom, position };
}

/**
 * Display URL stored under a keyed layout only (never reads the other layout).
 */
export function getKeyedLayoutDisplayUrl(
  imageCrop: unknown,
  layout: LinkCropLayout
): string | null {
  if (!isKeyedLinkImageCrop(imageCrop)) return null;
  const entry = (imageCrop as Record<string, unknown>)[layout];
  if (!entry || typeof entry !== "object") return null;
  const url = (entry as { image_url?: unknown }).image_url;
  return typeof url === "string" && url.trim() ? url.trim() : null;
}

/**
 * Resolve the crop for the layout we're leaving (source), never the target.
 * Keyed rows use only `sourceLayout`'s cropState + its own cropViewport.
 */
export function resolveExistingLinkCropArea(args: {
  imageCrop: unknown;
  targetLayout: LinkCropLayout;
  sourceViewport: CropViewportSize;
  naturalWidth: number;
  naturalHeight: number;
}): CropAreaPixels | null {
  const {
    imageCrop,
    targetLayout,
    sourceViewport,
    naturalWidth,
    naturalHeight,
  } = args;
  const sourceLayout = oppositeLinkLayout(targetLayout);

  if (isKeyedLinkImageCrop(imageCrop)) {
    const keyed = imageCrop as Record<string, unknown>;
    const raw = keyed[sourceLayout];
    if (raw && typeof raw === "object") {
      const entry = getLinkLayoutCropEntry(
        { [sourceLayout]: raw },
        sourceLayout
      );
      if (entry) {
        // Always use the source layout entry's viewport with its cropState —
        // do not substitute the target layout viewport.
        const vp =
          entry.cropViewport.width > 0 ? entry.cropViewport : sourceViewport;
        return getNaturalCropAreaFromViewportState(
          entry.cropState,
          naturalWidth,
          naturalHeight,
          vp
        );
      }
    }
    return null;
  }

  if (isFlatPersistedImageCrop(imageCrop)) {
    const entry = getLinkLayoutCropEntry(imageCrop, sourceLayout);
    if (!entry) return null;
    // Flat belongs to whichever viewport it was saved with (usually line/square).
    const flatVp =
      entry.cropViewport.width > 0 ? entry.cropViewport : sourceViewport;
    return getNaturalCropAreaFromViewportState(
      entry.cropState,
      naturalWidth,
      naturalHeight,
      flatVp
    );
  }

  return null;
}

/**
 * Target-layout cropState only (does not decide the PNG).
 * Prefers expanding into the original around prior content; cover-clips only
 * when the bitmap cannot contain that larger target-aspect window.
 */
export function cropStateForTargetLayoutContent(args: {
  contentOnOriginal: CropAreaPixels;
  targetViewport: CropViewportSize;
  naturalWidth: number;
  naturalHeight: number;
}): { cropState: CropViewportState; exportFrame: CropAreaPixels } {
  const targetAspect = args.targetViewport.width / args.targetViewport.height;
  const inImage = fitContentToAspectUsingOriginal(
    args.contentOnOriginal,
    targetAspect,
    args.naturalWidth,
    args.naturalHeight
  );
  const cropState = getViewportStateFromNaturalCropArea(
    inImage,
    args.naturalWidth,
    args.naturalHeight,
    args.targetViewport
  );
  // Window implied by the saved cropState (for reference / callers).
  const exportFrame = getNaturalCropAreaFromViewportState(
    cropState,
    args.naturalWidth,
    args.naturalHeight,
    args.targetViewport
  );
  return { cropState, exportFrame };
}

export type ExpandedLayoutCrop = {
  /** Zoom/pan for the target layout cropper / DB. */
  cropState: CropViewportState;
  /** Natural window used when building the layout-switch image. */
  exportFrame: CropAreaPixels;
};

/**
 * Map the previous layout's visible crop into the target aspect, preferring
 * extra pixels from the original before any equal-edge cover clipping.
 */
export function deriveExpandedLayoutCrop(args: {
  imageCrop: unknown;
  targetLayout: LinkCropLayout;
  targetViewport: CropViewportSize;
  sourceViewport: CropViewportSize;
  naturalWidth: number;
  naturalHeight: number;
}): ExpandedLayoutCrop | null {
  const existing = resolveExistingLinkCropArea({
    imageCrop: args.imageCrop,
    targetLayout: args.targetLayout,
    sourceViewport: args.sourceViewport,
    naturalWidth: args.naturalWidth,
    naturalHeight: args.naturalHeight,
  });
  if (!existing) return null;

  return cropStateForTargetLayoutContent({
    contentOnOriginal: existing,
    targetViewport: args.targetViewport,
    naturalWidth: args.naturalWidth,
    naturalHeight: args.naturalHeight,
  });
}

/** @deprecated Use deriveExpandedLayoutCrop */
export function deriveExpandedLayoutCropState(args: {
  imageCrop: unknown;
  targetLayout: LinkCropLayout;
  targetViewport: CropViewportSize;
  sourceViewport: CropViewportSize;
  naturalWidth: number;
  naturalHeight: number;
}): CropViewportState | null {
  return deriveExpandedLayoutCrop(args)?.cropState ?? null;
}

/** Alpha below this is treated as empty when trimming. */
const TRIM_ALPHA_MIN = 8;

/** Bounding box of non-transparent pixels, or null if fully empty. */
export function getOpaqueBounds(
  imageData: ImageData,
  alphaMin: number = TRIM_ALPHA_MIN
): CropAreaPixels | null {
  const { width, height, data } = imageData;
  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (data[(y * width + x) * 4 + 3] >= alphaMin) {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }

  if (maxX < minX || maxY < minY) return null;
  return {
    x: minX,
    y: minY,
    width: maxX - minX + 1,
    height: maxY - minY + 1,
  };
}

function canvasToPngFile(canvas: HTMLCanvasElement, fileName: string): Promise<File> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (result) => {
        if (result) resolve(new File([result], fileName, { type: "image/png" }));
        else reject(new Error("Failed to export cropped image"));
      },
      "image/png"
    );
  });
}

/**
 * Place the opaque region of `source` into a target-aspect canvas with a cover
 * fit (fills the canvas; no letterbox). Overflow is clipped equally on both
 * edges of the overflowing axis.
 */
export function fitOpaqueCoverToAspectCanvas(
  source: HTMLCanvasElement,
  outputWidth: number,
  outputHeight: number
): HTMLCanvasElement {
  const ctxIn = source.getContext("2d", { willReadFrequently: true });
  if (!ctxIn) {
    throw new Error("Unable to read image for trim");
  }
  const bounds =
    getOpaqueBounds(ctxIn.getImageData(0, 0, source.width, source.height)) || {
      x: 0,
      y: 0,
      width: source.width,
      height: source.height,
    };

  const out = document.createElement("canvas");
  out.width = outputWidth;
  out.height = outputHeight;
  const ctx = out.getContext("2d", { alpha: true });
  if (!ctx) {
    throw new Error("Unable to crop image");
  }
  ctx.clearRect(0, 0, outputWidth, outputHeight);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  if (!(bounds.width > 0 && bounds.height > 0)) {
    return out;
  }

  const scale = Math.max(
    outputWidth / bounds.width,
    outputHeight / bounds.height
  );
  const drawW = bounds.width * scale;
  const drawH = bounds.height * scale;
  const destX = (outputWidth - drawW) / 2;
  const destY = (outputHeight - drawH) / 2;
  ctx.drawImage(
    source,
    bounds.x,
    bounds.y,
    bounds.width,
    bounds.height,
    destX,
    destY,
    drawW,
    drawH
  );
  return out;
}

/** @deprecated Use fitOpaqueCoverToAspectCanvas */
export function fitOpaqueTopLeftToAspectCanvas(
  source: HTMLCanvasElement,
  outputWidth: number,
  outputHeight: number
): HTMLCanvasElement {
  return fitOpaqueCoverToAspectCanvas(source, outputWidth, outputHeight);
}

/**
 * Export `frame` (target aspect) into the output size. Portions of the frame
 * outside the bitmap are left clear (cover frames should stay in-bounds).
 */
export async function cropImageFrameToFile(args: {
  imageSrc: string;
  frame: CropAreaPixels;
  outputWidth: number;
  outputHeight: number;
  fileName: string;
}): Promise<File> {
  const canvas = await renderImageFrameToCanvas(args);
  return canvasToPngFile(canvas, args.fileName);
}

async function renderImageFrameToCanvas(args: {
  imageSrc: string;
  frame: CropAreaPixels;
  outputWidth: number;
  outputHeight: number;
}): Promise<HTMLCanvasElement> {
  const image = await loadImage(args.imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = args.outputWidth;
  canvas.height = args.outputHeight;
  const ctx = canvas.getContext("2d", { alpha: true });
  if (!ctx) {
    throw new Error("Unable to crop image");
  }

  ctx.clearRect(0, 0, args.outputWidth, args.outputHeight);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";

  const imgW = image.naturalWidth;
  const imgH = image.naturalHeight;
  const { frame } = args;

  if (!(frame.width > 0 && frame.height > 0)) {
    throw new Error("Invalid crop frame");
  }

  const ix1 = Math.max(frame.x, 0);
  const iy1 = Math.max(frame.y, 0);
  const ix2 = Math.min(frame.x + frame.width, imgW);
  const iy2 = Math.min(frame.y + frame.height, imgH);

  if (ix2 > ix1 && iy2 > iy1) {
    const sw = ix2 - ix1;
    const sh = iy2 - iy1;
    const destX = ((ix1 - frame.x) / frame.width) * args.outputWidth;
    const destY = ((iy1 - frame.y) / frame.height) * args.outputHeight;
    const destW = (sw / frame.width) * args.outputWidth;
    const destH = (sh / frame.height) * args.outputHeight;
    ctx.drawImage(image, ix1, iy1, sw, sh, destX, destY, destW, destH);
  }

  return canvas;
}

/**
 * Build the new layout image from the previous layout's visible content, then
 * derive cropState for the TARGET layout. Prefers expanding into the original
 * around that content; cover-clips only when needed (no letterbox).
 */
export async function createLayoutSwitchImage(args: {
  originalImageSrc: string;
  imageCrop: unknown;
  targetLayout: LinkCropLayout;
  targetViewport: CropViewportSize;
  sourceViewport: CropViewportSize;
  outputWidth: number;
  outputHeight: number;
  fileName: string;
  /** Prior layout display URL — trimmed first when it has letterbox transparency. */
  previousDisplayUrl?: string | null;
}): Promise<(ExpandedLayoutCrop & { file: File }) | null> {
  const original = await loadImage(args.originalImageSrc);
  const natW = original.naturalWidth;
  const natH = original.naturalHeight;
  const targetAspect = args.targetViewport.width / args.targetViewport.height;

  let contentOnOriginal = resolveExistingLinkCropArea({
    imageCrop: args.imageCrop,
    targetLayout: args.targetLayout,
    sourceViewport: args.sourceViewport,
    naturalWidth: natW,
    naturalHeight: natH,
  });

  // Trim transparent edges from the previous display image when present.
  if (args.previousDisplayUrl) {
    try {
      const prev = await loadImage(args.previousDisplayUrl);
      const prevCanvas = document.createElement("canvas");
      prevCanvas.width = prev.naturalWidth;
      prevCanvas.height = prev.naturalHeight;
      const prevCtx = prevCanvas.getContext("2d", { willReadFrequently: true });
      if (prevCtx) {
        prevCtx.drawImage(prev, 0, 0);
        const trimmed = getOpaqueBounds(
          prevCtx.getImageData(0, 0, prevCanvas.width, prevCanvas.height)
        );
        if (
          trimmed &&
          (trimmed.width < prevCanvas.width ||
            trimmed.height < prevCanvas.height) &&
          contentOnOriginal
        ) {
          contentOnOriginal = {
            x:
              contentOnOriginal.x +
              (trimmed.x / prevCanvas.width) * contentOnOriginal.width,
            y:
              contentOnOriginal.y +
              (trimmed.y / prevCanvas.height) * contentOnOriginal.height,
            width: (trimmed.width / prevCanvas.width) * contentOnOriginal.width,
            height:
              (trimmed.height / prevCanvas.height) * contentOnOriginal.height,
          };
        }
      }
    } catch {
      // Keep crop-area path when the previous display cannot be loaded.
    }
  }

  if (!contentOnOriginal) return null;

  // Trim transparent strips inside the crop window on the original.
  const probe = document.createElement("canvas");
  const probeScale = Math.min(
    1,
    512 / Math.max(contentOnOriginal.width, contentOnOriginal.height)
  );
  probe.width = Math.max(1, Math.round(contentOnOriginal.width * probeScale));
  probe.height = Math.max(1, Math.round(contentOnOriginal.height * probeScale));
  const probeCtx = probe.getContext("2d", { willReadFrequently: true });
  if (probeCtx) {
    probeCtx.clearRect(0, 0, probe.width, probe.height);
    probeCtx.drawImage(
      original,
      contentOnOriginal.x,
      contentOnOriginal.y,
      contentOnOriginal.width,
      contentOnOriginal.height,
      0,
      0,
      probe.width,
      probe.height
    );
    const probeBounds = getOpaqueBounds(
      probeCtx.getImageData(0, 0, probe.width, probe.height)
    );
    if (
      probeBounds &&
      (probeBounds.width < probe.width || probeBounds.height < probe.height)
    ) {
      contentOnOriginal = {
        x: contentOnOriginal.x + probeBounds.x / probeScale,
        y: contentOnOriginal.y + probeBounds.y / probeScale,
        width: probeBounds.width / probeScale,
        height: probeBounds.height / probeScale,
      };
    }
  }

  // ——— Image pipeline: expand into original first, then cover if needed ———
  // Grid→template grows top/bottom from the original so less of the grid
  // content is lost; only equal-edge clips when the bitmap has no more room.
  const coverFrame = fitContentToAspectUsingOriginal(
    contentOnOriginal,
    targetAspect,
    natW,
    natH
  );
  const framed = await renderImageFrameToCanvas({
    imageSrc: args.originalImageSrc,
    frame: coverFrame,
    outputWidth: args.outputWidth,
    outputHeight: args.outputHeight,
  });
  // Safety: if anything transparent remains, cover-fill the canvas (equal clip).
  const fitted = fitOpaqueCoverToAspectCanvas(
    framed,
    args.outputWidth,
    args.outputHeight
  );
  const file = await canvasToPngFile(fitted, args.fileName);

  // cropState only — derive from the image we just built (same path the
  // cropper uses: original vs cropped), so zoom/pan match without changing PNG.
  const previewUrl = URL.createObjectURL(file);
  let cropState: CropViewportState | null = null;
  try {
    cropState = await resolveCropViewportStateForEdit({
      cropState: null,
      originalUrl: args.originalImageSrc,
      croppedUrl: previewUrl,
      aspectRatio: targetAspect,
      viewport: args.targetViewport,
      // No defaultFit — if derive fails we use geometric fallback below.
    });
  } catch {
    cropState = null;
  } finally {
    URL.revokeObjectURL(previewUrl);
  }

  if (!cropState) {
    cropState = cropStateForTargetLayoutContent({
      contentOnOriginal,
      targetViewport: args.targetViewport,
      naturalWidth: natW,
      naturalHeight: natH,
    }).cropState;
  }

  const containScale = getContainScale(
    natW,
    natH,
    args.targetViewport.width,
    args.targetViewport.height
  );
  const scale = containScale * cropState.zoom;
  cropState = {
    zoom: cropState.zoom,
    position: clampCropPosition(
      cropState.position.x,
      cropState.position.y,
      scale,
      natW,
      natH,
      args.targetViewport.width,
      args.targetViewport.height,
      true
    ),
  };

  return {
    cropState,
    exportFrame: coverFrame,
    file,
  };
}

